/**
 * Oscar Club Dashboard JavaScript
 * Handles all dashboard interactions, API calls, and dynamic updates
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    apiBaseUrl: '/apps/oscar-club',
    tokenKey: 'oscar_club_token',
    tokenExpiry: 15 * 60 * 1000, // 15 minutes
    refreshInterval: 5 * 60 * 1000, // 5 minutes
  };

  // State management
  const state = {
    user: null,
    points: null,
    tier: null,
    quests: [],
    badges: [],
    referrals: null,
    isLoading: false,
    lastFetch: null,
  };

  // Main OscarClub object
  window.OscarClub = {
    // Initialization
    init() {
      this.setupEventListeners();
      this.loadAuthToken();
    },

    // Authentication & Token Management
    loadAuthToken() {
      const stored = localStorage.getItem(CONFIG.tokenKey);
      if (stored) {
        try {
          const { token, expiry } = JSON.parse(stored);
          if (Date.now() < expiry) {
            this.token = token;
            return;
          }
        } catch (e) {
          console.error('Error parsing stored token:', e);
        }
      }
      this.refreshToken();
    },

    async refreshToken() {
      try {
        const response = await this.apiCall('/auth/token', 'POST');
        if (response.token) {
          this.token = response.token;
          const expiry = Date.now() + CONFIG.tokenExpiry;
          localStorage.setItem(CONFIG.tokenKey, JSON.stringify({ token: response.token, expiry }));
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        this.showToast('Authentication error. Please refresh the page.', 'error');
      }
    },

    // API Calls
    async apiCall(endpoint, method = 'GET', data = null) {
      const url = `${CONFIG.apiBaseUrl}${endpoint}`;
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token || ''}`,
        },
      };

      if (data && (method === 'POST' || method === 'PUT')) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);

      if (response.status === 401) {
        await this.refreshToken();
        return this.apiCall(endpoint, method, data);
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const json = await response.json();
      // Unwrap the response if it has a data property
      return json.data || json;
    },

    // Data Loading Functions
    async loadHeroData() {
      try {
        const data = await this.apiCall('/dashboard/hero');
        state.points = data.points;
        state.tier = data.tier;

        this.updateHeroDisplay(data);
      } catch (error) {
        console.error('Error loading hero data:', error);
        this.showError('oscar-hero-points', 'Error loading data');
      }
    },

    updateHeroDisplay(data) {
      const pointsEl = document.getElementById('oscar-hero-points');
      const tierEl = document.getElementById('oscar-hero-tier');
      const progressEl = document.getElementById('oscar-hero-progress');

      if (pointsEl) {
        pointsEl.innerHTML = `<strong>${this.formatNumber(data.points.available)}</strong>`;
      }

      if (tierEl) {
        tierEl.innerHTML = this.renderTierBadge(data.tier.current);
      }

      if (progressEl && data.tier.next) {
        const progress = this.renderProgressBar(
          data.tier.pointsToNext,
          data.tier.nextTierThreshold - data.tier.currentTierThreshold
        );
        progressEl.innerHTML = progress;
      }
    },

    async loadStatsData() {
      try {
        const data = await this.apiCall('/dashboard/stats');

        this.updateStat('oscar-stat-total-points', this.formatNumber(data.totalPoints));
        this.updateStat('oscar-stat-available-points', this.formatNumber(data.availablePoints));
        this.updateStat('oscar-stat-completed-quests', data.completedQuests);
        this.updateStat('oscar-stat-total-quests', data.totalQuests);
        this.updateStat('oscar-stat-badges-earned', data.badgesEarned);
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    updateStat(elementId, value) {
      const el = document.getElementById(elementId);
      if (el) {
        el.textContent = value;
        el.closest('.oscar-loading-skeleton')?.classList.remove('oscar-loading-skeleton');
      }
    },

    async loadQuestsData() {
      try {
        const data = await this.apiCall('/quests');
        state.quests = data.quests;
        this.renderQuests(data.quests);
      } catch (error) {
        console.error('Error loading quests:', error);
        this.showError('oscar-quests-list', 'Error loading quests');
      }
    },

    renderQuests(quests) {
      const container = document.getElementById('oscar-quests-list');
      const empty = document.getElementById('oscar-quests-empty');
      const template = document.getElementById('oscar-quest-template');

      if (!container || !template) return;

      container.innerHTML = '';

      if (quests.length === 0) {
        container.style.display = 'none';
        if (empty) empty.style.display = 'block';
        return;
      }

      container.style.display = 'block';
      if (empty) empty.style.display = 'none';

      quests.forEach(quest => {
        const questHtml = this.renderQuestCard(quest, template);
        container.insertAdjacentHTML('beforeend', questHtml);
      });

      this.attachQuestEventListeners();
    },

    renderQuestCard(quest, template) {
      let html = template.innerHTML;

      html = html.replace('{QUEST_ID}', quest.id);
      html = html.replace('{STATUS}', quest.status);
      html = html.replace('{ICON}', this.getQuestIcon(quest.type));
      html = html.replace('{TITLE}', this.escapeHtml(quest.title));
      html = html.replace('{DESCRIPTION}', this.escapeHtml(quest.description));
      html = html.replace('{POINTS}', this.formatNumber(quest.points));
      html = html.replace('{TYPE}', quest.type);

      // Progress bar
      if (quest.progress && quest.required) {
        const progressBar = `
          <div class="oscar-progress-wrapper">
            ${this.renderProgressBar(quest.progress, quest.required)}
          </div>
        `;
        html = html.replace('{PROGRESS_BAR}', progressBar);
      } else {
        html = html.replace('{PROGRESS_BAR}', '');
      }

      // Expiry
      if (quest.expiresAt) {
        const expiryHtml = `<span class="oscar-quest-expiry">Expires: ${this.formatDate(quest.expiresAt)}</span>`;
        html = html.replace('{EXPIRES}', expiryHtml);
      } else {
        html = html.replace('{EXPIRES}', '');
      }

      // Action button
      let actionButton = '';
      if (quest.status === 'active' && quest.actionUrl) {
        actionButton = `<a href="${quest.actionUrl}" class="oscar-btn oscar-btn-primary">Start Quest</a>`;
      } else if (quest.status === 'completed') {
        actionButton = `<button class="oscar-btn oscar-btn-success" disabled>Completed ✓</button>`;
      }
      html = html.replace('{ACTION_BUTTON}', actionButton);

      return html;
    },

    filterQuests(filter) {
      const cards = document.querySelectorAll('.oscar-quest-card');
      cards.forEach(card => {
        const status = card.dataset.status;
        if (filter === 'all' || filter === status) {
          card.style.display = 'block';
        } else {
          card.style.display = 'none';
        }
      });
    },

    async loadBadgesData() {
      try {
        const data = await this.apiCall('/badges');
        state.badges = data.badges;
        this.renderBadges(data.badges);

        this.updateStat('oscar-badges-earned-count', data.earnedCount);
        this.updateStat('oscar-badges-total-count', data.totalCount);
      } catch (error) {
        console.error('Error loading badges:', error);
        this.showError('oscar-badges-grid', 'Error loading badges');
      }
    },

    renderBadges(badges) {
      const container = document.getElementById('oscar-badges-grid');
      const empty = document.getElementById('oscar-badges-empty');
      const template = document.getElementById('oscar-badge-template');

      if (!container || !template) return;

      container.innerHTML = '';

      if (badges.length === 0) {
        container.style.display = 'none';
        if (empty) empty.style.display = 'block';
        return;
      }

      container.style.display = 'grid';
      if (empty) empty.style.display = 'none';

      badges.forEach(badge => {
        const badgeHtml = this.renderBadgeCard(badge, template);
        container.insertAdjacentHTML('beforeend', badgeHtml);
      });

      this.attachBadgeEventListeners();
    },

    renderBadgeCard(badge, template) {
      let html = template.innerHTML;

      html = html.replace('{BADGE_ID}', badge.id);
      html = html.replace('{RARITY}', badge.rarity);
      html = html.replace('{STATUS}', badge.earned ? 'earned' : 'locked');
      html = html.replace('{NAME}', this.escapeHtml(badge.name));
      html = html.replace('{DESCRIPTION}', this.escapeHtml(badge.description));
      html = html.replace('{RARITY}', badge.rarity);

      // Badge icon
      const badgeIcon = `<div class="oscar-badge-icon-large" data-rarity="${badge.rarity}">${badge.icon || '🏅'}</div>`;
      html = html.replace('{BADGE_ICON}', badgeIcon);

      // Earned date
      if (badge.earnedAt) {
        const earnedHtml = `<span class="oscar-badge-earned-date">Earned ${this.formatDate(badge.earnedAt)}</span>`;
        html = html.replace('{EARNED_DATE}', earnedHtml);
      } else {
        html = html.replace('{EARNED_DATE}', '');
      }

      // Lock overlay
      if (!badge.earned) {
        const lockOverlay = `<div class="oscar-badge-lock"><span class="oscar-lock-icon">🔒</span></div>`;
        html = html.replace('{LOCK_OVERLAY}', lockOverlay);
      } else {
        html = html.replace('{LOCK_OVERLAY}', '');
      }

      return html;
    },

    filterBadges(filter) {
      const items = document.querySelectorAll('.oscar-badge-item');
      items.forEach(item => {
        const rarity = item.dataset.rarity;
        const status = item.dataset.status;

        if (filter === 'all' ||
            filter === status ||
            filter === rarity) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
    },

    async loadTierBenefitsData() {
      try {
        const data = await this.apiCall('/tiers');
        this.renderTierBenefits(data);
      } catch (error) {
        console.error('Error loading tier benefits:', error);
      }
    },

    renderTierBenefits(data) {
      const currentEl = document.getElementById('oscar-tier-current-benefits');
      const nextEl = document.getElementById('oscar-tier-next-benefits');
      const ladderEl = document.getElementById('oscar-tier-ladder');

      if (currentEl && data.current) {
        currentEl.innerHTML = this.renderTierCard(data.current, true);
      }

      if (nextEl && data.next) {
        nextEl.innerHTML = this.renderTierCard(data.next, false);
      }

      if (ladderEl && data.allTiers) {
        ladderEl.innerHTML = data.allTiers.map(tier =>
          this.renderTierLadderItem(tier, data.current.slug)
        ).join('');
      }

      // Update tier progress
      if (data.pointsToNext) {
        this.updateProgressBar('tier-progress', data.pointsProgress, data.pointsRequired);
        const pointsEl = document.getElementById('oscar-tier-progress-points');
        if (pointsEl) {
          pointsEl.innerHTML = `${this.formatNumber(data.pointsProgress)} / ${this.formatNumber(data.pointsRequired)}`;
        }
      }
    },

    renderTierCard(tier, isCurrent) {
      const benefits = tier.benefits.map(b => `<li>${this.escapeHtml(b)}</li>`).join('');
      const cta = isCurrent ? '' : `<button class="oscar-btn oscar-btn-primary">Unlock This Tier</button>`;

      return `
        ${this.renderTierBadge(tier.slug)}
        <h3 class="oscar-tier-name">${this.escapeHtml(tier.name)}</h3>
        <p class="oscar-tier-requirement">${this.formatNumber(tier.threshold)} points required</p>
        <ul class="oscar-tier-benefits-list">${benefits}</ul>
        ${cta}
      `;
    },

    renderTierLadderItem(tier, currentTierSlug) {
      const status = tier.slug === currentTierSlug ? 'current' : (tier.unlocked ? 'unlocked' : 'locked');
      const statusBadge = status === 'current' ? '<span class="oscar-status-current">Current</span>' : '';
      const benefits = tier.benefits.slice(0, 3).map(b => `<li>${this.escapeHtml(b)}</li>`).join('');

      return `
        <div class="oscar-tier-ladder-item" data-tier="${tier.slug}" data-status="${status}">
          <div class="oscar-tier-ladder-badge">
            ${this.renderTierBadge(tier.slug)}
          </div>
          <div class="oscar-tier-ladder-info">
            <h4 class="oscar-tier-ladder-name">${this.escapeHtml(tier.name)}</h4>
            <p class="oscar-tier-ladder-requirement">${this.formatNumber(tier.threshold)} points</p>
            <ul class="oscar-tier-ladder-benefits">${benefits}</ul>
          </div>
          <div class="oscar-tier-ladder-status">${statusBadge}</div>
        </div>
      `;
    },

    async loadReferralData() {
      try {
        const data = await this.apiCall('/referrals');
        state.referrals = data;

        const codeInput = document.getElementById('oscar-referral-code');
        const linkInput = document.getElementById('oscar-referral-link');

        if (codeInput) codeInput.value = data.code;
        if (linkInput) linkInput.value = data.link;

        this.updateStat('oscar-referral-count', data.count);
        this.updateStat('oscar-referral-points-earned', this.formatNumber(data.pointsEarned));

        this.renderReferralList(data.recent);
      } catch (error) {
        console.error('Error loading referral data:', error);
      }
    },

    renderReferralList(referrals) {
      const container = document.getElementById('oscar-referral-list');
      const template = document.getElementById('oscar-referral-item-template');

      if (!container || !template || !referrals) return;

      const listHtml = referrals.map(ref => {
        let html = template.innerHTML;
        html = html.replace('{NAME}', this.escapeHtml(ref.name));
        html = html.replace('{DATE}', this.formatDate(ref.date));
        html = html.replace('{POINTS}', ref.points);
        return html;
      }).join('');

      container.innerHTML = `<h4 class="oscar-referral-list-title">Recent Referrals</h4>${listHtml}`;
    },

    async loadFloatingBadgeData() {
      try {
        const data = await this.apiCall('/dashboard/hero');

        const valueEl = document.getElementById('oscar-floating-points-value');
        const availableEl = document.getElementById('oscar-tooltip-available');
        const tierEl = document.getElementById('oscar-tooltip-tier');

        if (valueEl) valueEl.textContent = this.formatNumber(data.points.available);
        if (availableEl) availableEl.textContent = this.formatNumber(data.points.available);
        if (tierEl) tierEl.textContent = data.tier.current.name;
      } catch (error) {
        console.error('Error loading floating badge data:', error);
      }
    },

    // Helper: Render components
    renderTierBadge(tierSlug) {
      const tiers = {
        puppy: { name: 'Puppy', icon: '🐕', color: '#8B6F47' },
        buddy: { name: 'Buddy', icon: '🦴', color: '#C0C0C0' },
        bestfriend: { name: 'Best Friend', icon: '⭐', color: '#F4B350' },
        vip: { name: 'VIP Golden Pup', icon: '👑', color: '#FFD700' },
      };
      const tier = tiers[tierSlug] || tiers.puppy;
      return `<span class="oscar-tier-badge" style="--tier-color: ${tier.color};">${tier.icon} ${tier.name}</span>`;
    },

    renderProgressBar(current, total) {
      const percentage = total > 0 ? Math.min(Math.round((current / total) * 100), 100) : 0;
      return `
        <div class="oscar-progress-bar-wrapper">
          <div class="oscar-progress-track">
            <div class="oscar-progress-fill" style="width: ${percentage}%"></div>
          </div>
          <span class="oscar-progress-text">${current} / ${total}</span>
        </div>
      `;
    },

    // User Actions
    copyToClipboard(inputId, message) {
      const input = document.getElementById(inputId);
      if (!input) return;

      input.select();
      document.execCommand('copy');
      this.showToast(message || 'Copied to clipboard!', 'success');
    },

    async shareOnSocial(platform) {
      const code = state.referrals?.code;
      const link = state.referrals?.link;

      if (!link) {
        this.showToast('Referral link not available', 'error');
        return;
      }

      const text = `Join Oscar Club and get exclusive rewards! Use my referral code: ${code}`;
      const encodedText = encodeURIComponent(text);
      const encodedLink = encodeURIComponent(link);

      const urls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedLink}`,
        whatsapp: `https://wa.me/?text=${encodedText}%20${encodedLink}`,
        email: `mailto:?subject=Join Oscar Club&body=${encodedText}%0A%0A${encodedLink}`,
      };

      const url = urls[platform];
      if (url) {
        window.open(url, '_blank', 'width=600,height=400');
      }
    },

    // UI Helpers
    showToast(message, type = 'info') {
      const toast = document.createElement('div');
      toast.className = `oscar-toast oscar-toast-${type}`;
      toast.textContent = message;
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'polite');

      document.body.appendChild(toast);

      setTimeout(() => toast.classList.add('oscar-toast-show'), 10);
      setTimeout(() => {
        toast.classList.remove('oscar-toast-show');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    },

    showError(containerId, message) {
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = `
          <div class="oscar-error-state">
            <p class="oscar-error-message">${this.escapeHtml(message)}</p>
            <button class="oscar-btn oscar-btn-secondary" onclick="location.reload()">Retry</button>
          </div>
        `;
      }
    },

    // Utility Functions
    formatNumber(num) {
      return new Intl.NumberFormat('en-US').format(num);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    getQuestIcon(type) {
      const icons = {
        purchase: '🛍️',
        review: '⭐',
        social: '📱',
        referral: '👥',
        birthday: '🎂',
        daily: '📅',
      };
      return icons[type] || '🎯';
    },

    // Event Listeners
    setupEventListeners() {
      // Auto-refresh data periodically
      setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.refreshDashboardData();
        }
      }, CONFIG.refreshInterval);

      // Refresh on visibility change
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' &&
            Date.now() - (state.lastFetch || 0) > CONFIG.refreshInterval) {
          this.refreshDashboardData();
        }
      });
    },

    attachQuestEventListeners() {
      // Add quest-specific event listeners here
    },

    attachBadgeEventListeners() {
      const badges = document.querySelectorAll('.oscar-badge-item');
      badges.forEach(badge => {
        badge.addEventListener('click', () => {
          const badgeId = badge.dataset.badgeId;
          this.showBadgeModal(badgeId);
        });
      });
    },

    showBadgeModal(badgeId) {
      const badge = state.badges.find(b => b.id === badgeId);
      if (!badge) return;

      const modal = document.getElementById('oscar-badge-modal');
      const body = document.getElementById('oscar-badge-modal-body');

      if (!modal || !body) return;

      body.innerHTML = `
        <div class="oscar-modal-badge-details">
          <div class="oscar-badge-icon-large">${badge.icon || '🏅'}</div>
          <h2>${this.escapeHtml(badge.name)}</h2>
          <p class="oscar-badge-rarity">${badge.rarity}</p>
          <p>${this.escapeHtml(badge.description)}</p>
          ${badge.earned ? `<p class="oscar-earned-date">Earned: ${this.formatDate(badge.earnedAt)}</p>` : ''}
        </div>
      `;

      modal.style.display = 'flex';
      modal.querySelector('.oscar-modal-close').addEventListener('click', () => {
        modal.style.display = 'none';
      });
    },

    async refreshDashboardData() {
      if (state.isLoading) return;

      state.isLoading = true;
      state.lastFetch = Date.now();

      try {
        await Promise.all([
          this.loadHeroData(),
          this.loadStatsData(),
          this.loadFloatingBadgeData(),
        ]);
      } catch (error) {
        console.error('Error refreshing dashboard:', error);
      } finally {
        state.isLoading = false;
      }
    },
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.OscarClub.init());
  } else {
    window.OscarClub.init();
  }
})();
